import deepcopy from 'deep-copy'
import * as types from './mutation-types'
import { getArticleErpAttributs } from '../../../api/article'
import { getOptionsArticles } from '../../../api/optionsArticle'
import { saveRegroupement, deleteRegroupement, getRegroupements } from '../../../api/regroupement'

const initialState = {
    currentRegroupement: {
        id: null,
        articleMaitre: {
            codeArticle: null,
            codeDeclinaison: null
        },
        titre: null,
        canonicalTag: null,
        articles: [],
        attributs: [],
        availableAttributs: []
    },
    regroupements: {
        list: {},
        pages: 1,
        page: 1,
        loading: false
    }
}

const state = deepcopy(initialState)

const mutations = {
    [types.SET_REGROUPEMENTS](state, { list, pages }) {
        state.regroupements = {
            ...state.regroupements,
            list,
            pages
        }
    },
    [types.SET_REGROUPEMENTS_LOADING]({ regroupements }, status) {
        Vue.set(regroupements, 'loading', status)
    },
    [types.SET_REGROUPEMENTS_PAGE]({ regroupements }, page) {
        Vue.set(regroupements, 'page', page)
    },
    [types.UPDATE_REGROUPEMENT](state, regroupement) {
        state.regroupements.list = {
            ...state.regroupements.list,
            [regroupement.id]: regroupement
        }
    },
    [types.DELETE_REGROUPEMENT](state, id) {
        const { list } = state.regroupements
        const filteredRegroupements = Object.keys(list)
            .filter(loopRegroupementId => loopRegroupementId !== id)
            .reduce((obj, subLoopId) => {
                obj[subLoopId] = list[subLoopId]
                return obj
            }, {})
        state.regroupements = {
            ...state.regroupements,
            list: { ...filteredRegroupements }
        }
    },
    [types.SET_CURRENT_REGROUPEMENT](state, regroupementId) {
        const regroupement = { ...state.regroupements.list[regroupementId] }
        const articles = regroupement.articles.reduce((obj, article) => {
            const { codeDeclinaison } = article
            if (codeDeclinaison !== null && codeDeclinaison.length !== 0) {
                obj[`${article.codeArticle}.${codeDeclinaison}`] = article
                return obj
            }
            obj[article.codeArticle] = article
            return obj
        }, {})
        regroupement.articles = articles
        state.currentRegroupement = {
            ...state.regroupements.list[regroupementId],
            articles,
            id: regroupementId
        }
    },
    [types.RESET_CURRENT_REGROUPEMENT](state) {
        state.currentRegroupement = deepcopy(initialState.currentRegroupement)
    },
    [types.SET_REGROUPEMENT_MASTER_ARTICLE]({ currentRegroupement }, { codeArticle, codeDeclinaison }) {
        codeDeclinaison = codeDeclinaison.length !== 0 ? codeDeclinaison : null
        Vue.set(currentRegroupement, 'articleMaitre', { codeArticle, codeDeclinaison })
    },
    [types.SET_REGROUPEMENT_ARTICLES]({ currentRegroupement }, articles) {
        Vue.set(currentRegroupement, 'articles', articles)
    },
    [types.ADD_REGROUPEMENT_AVAILABLE_ATTRIBUTS]({ currentRegroupement }, attributs) {
        state.currentRegroupement = {
            ...state.currentRegroupement,
            availableAttributs: { ...attributs }
        }
    },
    [types.ADD_REGROUPEMENT_ATTRIBUT](state, { nature = 'OPT', propriete, suffixe = null }) {
        state.currentRegroupement.attributs = [
            ...state.currentRegroupement.attributs,
            {
                affichage: 'SEL',
                nature,
                propriete
            }
        ]
    },
    [types.REMOVE_REGROUPEMENT_ATTRIBUT]({ currentRegroupement }, index) {
        let { attributs } = currentRegroupement
        attributs = attributs.splice(index, 1)
    },
    [types.SET_REGROUPEMENT_TITLE]({ currentRegroupement }, title) {
        Vue.set(currentRegroupement, 'titre', title)
    },
    [types.SET_REGROUPEMENT_CANONICAL_TAG]({ currentRegroupement }, canonicalTag) {
        Vue.set(currentRegroupement, 'canonicalTag', canonicalTag)
    }
}

const actions = {
    async getRegroupements({ state, commit }) {
        commit(types.SET_REGROUPEMENTS_LOADING, true)
        const regroupementsObj = await getRegroupements({ page: state.regroupements.page })
        commit(types.SET_REGROUPEMENTS_LOADING, false)
        commit(types.SET_REGROUPEMENTS, regroupementsObj)
    },
    setRegroupementsPage({ commit, dispatch }, page) {
        dispatch('setLoadingRegroupements', true)
        commit(types.SET_REGROUPEMENTS_PAGE, page)
        dispatch('getRegroupements')
    },
    setCurrentRegroupement({ state, commit, dispatch }, id) {
        commit(types.SET_CURRENT_REGROUPEMENT, id)
    },
    resetCurrentRegroupement({ commit }) {
        commit(types.RESET_CURRENT_REGROUPEMENT)
    },
    setRegroupementMasterArticle({ state, commit, dispatch }, article) {
        commit(types.SET_REGROUPEMENT_MASTER_ARTICLE, article)
        if (state.currentRegroupement.titre === null) {
            dispatch('setRegroupementTitle', article.designation1)
        }
    },
    setRegroupementArticles({ commit, dispatch }, articles) {
        commit(types.SET_REGROUPEMENT_ARTICLES, articles)
        dispatch('setRegroupementMasterArticle', Object.values(articles)[0])
    },
    setRegroupementTitle({ commit }, title) {
        commit(types.SET_REGROUPEMENT_TITLE, title)
    },
    setRegroupementCanonicalTag({ commit }, canonicalTag) {
        commit(types.SET_REGROUPEMENT_CANONICAL_TAG, canonicalTag)
    },
    async setRegroupementAttributs({ state, commit }) {
        const { articles } = state.currentRegroupement
        const BANNED_OPTIONS = [
            'DESIGNATION_COMMERCIALE',
            'DESCRIPTIF',
            'ZOOM',
            'MINIATURE',
            'VIGNETTE'
        ]
        const optionsArticles = await getOptionsArticles(
            Object.keys(articles)
        )
        const attributsErp = await getArticleErpAttributs(state.currentRegroupement.articleMaitre.codeArticle)
        const attributsOpt = optionsArticles.length !== 0
            ? Object.values(optionsArticles)[0].filter(option => {
                return !BANNED_OPTIONS.includes(option.propriete)
            })
            : []
        commit(types.ADD_REGROUPEMENT_AVAILABLE_ATTRIBUTS, [
            ...attributsOpt,
            ...attributsErp
        ])
    },
    selectAttribut({ state, commit }, availableAttribut) {
        const attributIndex = state.currentRegroupement.attributs.findIndex(attribut => {
            let { nature } = availableAttribut
            nature = nature !== undefined ? nature : 'OPT'
            return (
                attribut.nature === nature
                && attribut.propriete === availableAttribut.propriete
            )
        })
        if (attributIndex === -1) {
            commit(types.ADD_REGROUPEMENT_ATTRIBUT, availableAttribut)
            return true
        }
        commit(types.REMOVE_REGROUPEMENT_ATTRIBUT, attributIndex)
        return true
    },
    async saveRegroupement({ state, commit, dispatch }) {
        const {
            id,
            articleMaitre,
            titre,
            canonicalTag,
            articles,
            attributs
        } = state.currentRegroupement
        const dbRegroupement = await saveRegroupement({
            id,
            articleMaitre,
            titre,
            canonicalTag,
            attributs,
            articles: Object.values(articles)
        })
        commit(types.UPDATE_REGROUPEMENT, dbRegroupement)
        dispatch('toggleRegroupementEditMode')
    },
    async deleteRegroupement({ state, commit, dispatch }) {
        const regroupementId = state.currentRegroupement.id
        await deleteRegroupement(regroupementId)
        commit(types.DELETE_REGROUPEMENT, regroupementId)
        dispatch('toggleRegroupementEditMode')
    },
    clickPlanVenteRegroupement({ dispatch }, selection) {
      dispatch('resetPageFilteredArticles')
      dispatch('getFilteredArticles', true)
    }
}

const getters = {
    regroupements: state => state.regroupements.list,
    regroupementsPages: state => state.regroupements.pages,
    regroupementsCurrent: state => state.regroupements.page,
    currentRegroupement: state => state.currentRegroupement,
    currentRegroupementArticles: state => state.currentRegroupement.articles,
    currentRegroupementArticleMaitre: state => state.currentRegroupement.articleMaitre,
    currentRegroupementAttributs: state => state.currentRegroupement.attributs,
    currentRegroupementAvailableAttributs: state => state.currentRegroupement.availableAttributs
}

export default { state, mutations, actions, getters }
